import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { type MessageKey, toggleLocale, useLocale, useT } from "@trust/i18n";
import { useCallback, useEffect, useRef, useState } from "react";
import { listSwipersPublic } from "@/features/admin/api";

/**
 * 落地首页(任务 P0.8)—— 全屏落地形态,不套 PortalHeader,挂在根路由.
 * 复刻自旧站 fastj/inxguide-www/src/pages/{index,Carousel,Overlay,BgImg}.vue:
 * - 100vh 全屏轮播,4 张 slide(平台介绍/标准注册/标准搜索/标准排名),
 *   10s 自动切换 + 底部指示点;入场动画:左侧滑入过冲到 20% 再回弹归位
 *   (旧站 slide-left transition 0.5s + slide-back 0.3s 合并为一段 keyframes 0.8s)
 * - 第 1 张正文 HTML 来自后端 GET /api/v1/swipers 第一条 content/content_en
 *   (按语言,dangerouslySetInnerHTML 等价旧站 v-html),无数据时用兜底文案
 * - 右侧色块(400px)+ 右下角六边形装饰图(1s 后 clip-path 揭示,对应旧站 BgImg)
 * - 顶栏:logo(slide 4 黄底切深色版)+ 平台名 + 80px 汉堡
 * - 汉堡触发右侧滑入的橙色(#e74f29)全屏导航 Overlay(0.2s)
 * - 左下角:语言切换(中文(简体) / English,对应旧站 i18n-selector)+ 版权/备案
 * 与旧站的有意差异:旧站 v-show 切 slide 时旧帧立即消失(无滑出动画),此处保持一致;
 * 导航由 location.href 整页跳转改为 <Link> SPA 路由;配图从 assets 动态导入改为 /portal 静态路径。
 */

interface SlideDef {
  key: string;
  color: string;
  bgColor: string;
  rightBgColor: string;
  img: string;
  url: string;
  titleKey?: MessageKey;
  subtitleKey?: MessageKey;
  buttonKey: MessageKey;
}

const SLIDES: SlideDef[] = [
  {
    key: "intro",
    color: "#FFFFFF",
    bgColor: "rgb(29, 43, 69)",
    rightBgColor: "rgb(151, 181, 179)",
    img: "/portal/shape3.png",
    url: "/inx/register",
    buttonKey: "inx.landing.joinUs",
  },
  {
    key: "register",
    color: "#FFFFFF",
    bgColor: "rgb(103, 145, 188)",
    rightBgColor: "rgb(0, 141, 143)",
    img: "/portal/shape5.png",
    url: "/inx/regInx",
    titleKey: "inx.landing.c1Title",
    subtitleKey: "inx.landing.c1Subtitle",
    buttonKey: "inx.landing.c1Button",
  },
  {
    key: "search",
    color: "#FFFFFF",
    bgColor: "rgb(0, 141, 143)",
    rightBgColor: "rgb(231, 79, 41)",
    img: "/portal/shape2.png",
    url: "/inx/search",
    titleKey: "inx.landing.c2Title",
    subtitleKey: "inx.landing.c2Subtitle",
    buttonKey: "inx.landing.c2Button",
  },
  {
    key: "rank",
    color: "#333333",
    bgColor: "rgb(248, 214, 0)",
    rightBgColor: "rgb(103, 145, 188)",
    img: "/portal/shape4.png",
    url: "/inx/rank",
    titleKey: "inx.landing.c3Title",
    subtitleKey: "inx.landing.c3Subtitle",
    buttonKey: "inx.landing.c3Button",
  },
];

const NAV_LINKS: { path: string; labelKey: MessageKey }[] = [
  { path: "/", labelKey: "inx.header.home" },
  { path: "/inx/search", labelKey: "inx.header.standardsLibrary" },
  { path: "/inx/regInx", labelKey: "inx.header.registerStandard" },
  { path: "/inx/myInx", labelKey: "inx.header.myStandard" },
  { path: "/inx/rank", labelKey: "inx.header.standardRanking" },
  { path: "/inx/about", labelKey: "inx.header.aboutUs" },
];

const SLIDE_INTERVAL = 10_000;
/** 旧站入场 0.5s(transition)+ 回弹 0.3s(slide-back)合并后的总时长 */
const SLIDE_ANIMATION_MS = 800;

/* 轮播/配图动画 keyframes,类名加 landing- 前缀避免与全局冲突 */
const LANDING_CSS = `
/* 本页 w-full + padding 布局依赖 border-box(无 preflight,默认 content-box 会撑破视口) */
.landing-root,
.landing-root * {
  box-sizing: border-box;
}
.landing-slide {
  animation: landing-slide-in ${SLIDE_ANIMATION_MS}ms ease-in-out;
}
@keyframes landing-slide-in {
  0% { transform: translateX(-100%); opacity: 0; }
  62.5% { transform: translateX(20%); opacity: 1; }
  100% { transform: translateX(0); opacity: 1; }
}
.landing-shape {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* backwards fill:延迟 1s 内保持 from 态(完全裁剪不可见),对应旧站 showImg 延迟 1s 挂载 */
  animation: landing-img-reveal 1s linear 1s both;
}
@keyframes landing-img-reveal {
  from { clip-path: inset(0 0 0 100%); }
  to { clip-path: inset(0 0 0 0); }
}
`;

function MenuIcon() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 7h16M4 12h16M4 17h16"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="1em" height="1em" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" />
      <path d="M9 9l6 6M15 9l-6 6" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}

export function HomePage() {
  const t = useT();
  const locale = useLocale();

  const [activeIndex, setActiveIndex] = useState(0);
  const activeRef = useRef(0);
  const animatingRef = useRef(false);
  /** 首帧不播入场动画(旧站 transition-group 无 appear) */
  const enteredOnceRef = useRef(false);

  const goToSlide = useCallback((next: number) => {
    if (animatingRef.current || next === activeRef.current) return;
    animatingRef.current = true;
    activeRef.current = next;
    enteredOnceRef.current = true;
    setActiveIndex(next);
    window.setTimeout(() => {
      animatingRef.current = false;
    }, SLIDE_ANIMATION_MS);
  }, []);

  // 自动轮播 10s;手动切换后重置计时(activeIndex 变化即重建 interval)
  useEffect(() => {
    const timer = setInterval(() => {
      goToSlide((activeIndex + 1) % SLIDES.length);
    }, SLIDE_INTERVAL);
    return () => clearInterval(timer);
  }, [activeIndex, goToSlide]);

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [overlayMounted, setOverlayMounted] = useState(false);
  const [overlayShown, setOverlayShown] = useState(false);
  // Overlay 右侧滑入/滑出(0.2s):先挂载再下一帧 translate-x-0;关闭时滑出后卸载
  useEffect(() => {
    if (overlayOpen) {
      setOverlayMounted(true);
      const raf = requestAnimationFrame(() => requestAnimationFrame(() => setOverlayShown(true)));
      return () => cancelAnimationFrame(raf);
    }
    setOverlayShown(false);
    const timer = window.setTimeout(() => setOverlayMounted(false), 200);
    return () => clearTimeout(timer);
  }, [overlayOpen]);

  const swipersQ = useQuery({
    queryKey: ["public-swipers"],
    queryFn: listSwipersPublic,
  });
  const firstSwiper = swipersQ.data?.[0];
  const introHtml = (locale === "en" ? firstSwiper?.content_en : firstSwiper?.content) ?? "";

  const slide = SLIDES[activeIndex];

  return (
    <div className="landing-root overflow-hidden">
      <style>{LANDING_CSS}</style>

      {/* 顶栏:logo + 平台名 + 汉堡(文字色随 slide 切换);移动端收窄:70px 高、小 logo、18px 标题 */}
      <header
        className="fixed top-0 left-0 z-10 flex h-[140px] w-full items-center justify-between px-[60px] max-portal:h-[70px] max-portal:px-5"
        style={{ color: slide.color }}
      >
        <div className="relative flex h-[100px] flex-1 items-center text-[40px] font-medium max-portal:h-auto max-portal:text-[18px]">
          <img
            src={activeIndex < 3 ? "/portal/logo1.png" : "/portal/logo.png"}
            alt="TRUST"
            className="h-[40px] max-portal:h-[28px]"
          />
          <span
            className={`ml-[20px] font-bold max-portal:ml-2 ${locale === "en" ? "text-[22px] max-portal:text-[15px]" : ""}`}
          >
            {t("inx.title")}
          </span>
        </div>
        <button
          type="button"
          aria-label="menu"
          onClick={() => setOverlayOpen(true)}
          className="cursor-pointer border-0 bg-transparent p-0 text-[80px] leading-none text-white max-portal:text-[36px]"
        >
          <MenuIcon />
        </button>
      </header>

      {/* 全屏轮播 */}
      <div
        className="relative h-screen w-screen overflow-hidden"
        style={{ backgroundColor: slide.bgColor }}
      >
        <div
          key={slide.key}
          className={`absolute inset-0 flex items-center justify-center text-white ${
            enteredOnceRef.current ? "landing-slide" : ""
          }`}
        >
          {/* 右侧色块(移动端隐藏,400px 会盖住整个窄屏) */}
          <div
            className="absolute top-0 right-0 z-0 h-full w-[400px] max-portal:hidden"
            style={{ backgroundColor: slide.rightBgColor }}
          />

          {activeIndex === 0 ? (
            /* 第 1 张:平台介绍,正文 HTML 来自 swipers 接口;移动端去掉 450px 右留白 */
            <div className="whitespace-normal px-[50px] pt-[40px] pr-[450px] max-portal:px-6 max-portal:pr-6">
              {introHtml ? (
                <div
                  className={`${locale === "en" ? "text-[22px]" : "text-[26px]"} max-portal:text-[16px]`}
                  // biome-ignore lint/security/noDangerouslySetInnerHtml: 等价旧站 v-html,内容为后台 swipers 管理端录入的受信 HTML
                  dangerouslySetInnerHTML={{ __html: introHtml }}
                />
              ) : (
                <div
                  className={`${locale === "en" ? "text-[22px]" : "text-[26px]"} max-portal:text-[16px]`}
                >
                  <h4>{t("inx.aboutUs.subTitle")}</h4>
                  <p>{t("inx.aboutUs.p2")}</p>
                </div>
              )}
              <div className="mt-[20px]">
                <Link
                  to={slide.url}
                  className="relative z-[1] block h-[60px] w-[200px] rounded-lg border-2 px-[20px] text-center text-[32px] leading-[60px] no-underline max-portal:h-[44px] max-portal:w-[140px] max-portal:text-[18px] max-portal:leading-[40px]"
                  style={{ color: slide.color, borderColor: slide.color }}
                >
                  {t(slide.buttonKey)}
                </Link>
              </div>
            </div>
          ) : (
            /* 第 2-4 张:标题 + 副标题 + 描边按钮;移动端去掉大留白、650px 定宽改全宽 */
            <div
              className="relative flex h-full w-full items-center justify-center p-[60px] pr-[200px] max-portal:p-6"
              style={{ color: slide.color }}
            >
              <div className="absolute z-[1] flex h-full w-full items-center justify-center p-[60px] pr-[400px] max-portal:p-6">
                <div className="h-[200px] w-[650px] max-portal:h-auto max-portal:w-full">
                  <div className="break-all text-[50px] font-bold max-portal:text-[28px]">
                    {slide.titleKey ? t(slide.titleKey) : ""}
                  </div>
                  <div className="mt-[20px] text-[32px] font-medium max-portal:text-[18px]">
                    {slide.subtitleKey ? t(slide.subtitleKey) : ""}
                  </div>
                  <div className="mt-[20px]">
                    <Link
                      to={slide.url}
                      className="relative z-[1] block h-[60px] w-[240px] rounded-lg border-2 px-[20px] text-center text-[28px] leading-[60px] no-underline max-portal:h-[44px] max-portal:w-[180px] max-portal:text-[18px] max-portal:leading-[40px]"
                      style={{ color: slide.color, borderColor: slide.color }}
                    >
                      {t(slide.buttonKey)}
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 六边形装饰图(移动端隐藏,避免盖住正文) */}
          <img src={slide.img} alt="" className="landing-shape max-portal:hidden" />
        </div>

        {/* 指示点(移动端上移,避开两行底栏) */}
        <div className="absolute bottom-[20px] left-1/2 z-[1] flex -translate-x-1/2 gap-[10px] max-portal:bottom-[76px]">
          {SLIDES.map((s, i) => (
            <button
              key={s.key}
              type="button"
              aria-label={`slide ${i + 1}`}
              onClick={() => goToSlide(i)}
              className="h-[15px] w-[15px] cursor-pointer rounded-full border-0 bg-white p-0 transition-opacity duration-300"
              style={{ opacity: i === activeIndex ? 1 : 0.5 }}
            />
          ))}
        </div>
      </div>

      {/* 橙色全屏导航 Overlay(右侧滑入);移动端改单列布局、收窄留白与字号 */}
      {overlayMounted && (
        <div
          className={`fixed inset-0 z-20 bg-[#e74f29] text-white transition-transform duration-200 ease-in-out ${
            overlayShown ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="relative h-full w-full p-[30px] px-[40px] max-portal:p-5">
            <div className="absolute top-0 right-0 p-[30px] px-[60px] text-right max-portal:p-5">
              <button
                type="button"
                aria-label="close"
                onClick={() => setOverlayOpen(false)}
                className="cursor-pointer border-0 bg-transparent p-0 text-[80px] leading-none text-white max-portal:text-[36px]"
              >
                <CloseIcon />
              </button>
            </div>

            <div className="flex justify-between max-portal:flex-col">
              <div className="flex h-[80px] flex-1 items-center text-[40px] font-medium leading-[60px] max-portal:h-auto max-portal:pr-12 max-portal:text-[18px] max-portal:leading-normal">
                <img src="/portal/logo.png" alt="TRUST" className="h-[40px] max-portal:h-[28px]" />
                <span
                  className={`ml-[10px] ${locale === "en" ? "text-[24px] leading-[24px] max-portal:text-[15px]" : ""}`}
                >
                  {t("inx.title")}
                </span>
              </div>

              <div
                className={`pr-[220px] text-[40px] font-medium max-portal:pr-0 ${
                  locale === "en" ? "pr-[160px]" : ""
                }`}
              >
                {NAV_LINKS.map((link) => (
                  <div
                    key={link.path}
                    className="m-[20px] h-[40px] max-portal:m-0 max-portal:my-4 max-portal:h-auto"
                  >
                    <Link
                      to={link.path}
                      onClick={() => setOverlayOpen(false)}
                      className="cursor-pointer text-[28px] font-bold leading-[26px] text-white no-underline hover:underline max-portal:text-[20px]"
                    >
                      {t(link.labelKey)}
                    </Link>
                  </div>
                ))}
              </div>
            </div>

            <div className="absolute bottom-0 left-0 h-[80px] px-[60px] text-[16px] font-medium max-portal:h-auto max-portal:px-5 max-portal:pb-5 max-portal:text-[12px]">
              <div>Copyright © 2002-2024 {t("inx.title")}. All rights reserved</div>
            </div>
          </div>
        </div>
      )}

      {/* 左下角:语言切换 + 版权/备案(移动端改两行小字) */}
      <div className="fixed bottom-0 left-0 z-10 flex h-[60px] items-center text-center text-white max-portal:h-auto max-portal:flex-col max-portal:items-start max-portal:py-2">
        <div className="pl-[20px]">
          <div className="flex text-[16px] max-portal:text-[12px]">
            <button
              type="button"
              onClick={() => locale !== "zh" && toggleLocale()}
              className={`cursor-pointer border-0 bg-transparent p-0 text-[16px] max-portal:text-[12px] ${
                locale === "zh" ? "text-white" : "text-[#999999]"
              }`}
            >
              中文(简体)
            </button>
            <span className="text-[#999999]"> / </span>
            <button
              type="button"
              onClick={() => locale !== "en" && toggleLocale()}
              className={`cursor-pointer border-0 bg-transparent p-0 text-[16px] max-portal:text-[12px] ${
                locale === "en" ? "text-white" : "text-[#999999]"
              }`}
            >
              English
            </button>
          </div>
        </div>
        <div className="flex items-center pl-[20px] text-[16px] max-portal:text-[12px]">
          Copyright © 2002-2024
          <a
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noreferrer"
            className="ml-[10px] text-white no-underline"
          >
            苏ICP备2024132513号-1
          </a>
        </div>
      </div>
    </div>
  );
}
